package com.smashvoter.api

import kotlinx.serialization.Serializable

@Serializable
data class PersonProfile(
    val id: String,
    val name: String,
    val photoUrl: String
)

@Serializable
data class RankedPerson(
    val id: String,
    val name: String,
    val photoUrl: String
)

@Serializable
data class VotePairItem(
    val id: String,
    val person1: RankedPerson,
    val person2: RankedPerson,
    val used: Boolean
)

@Serializable
data class VotePairsResponse(
    val pairs: List<VotePairItem>,
    val votesMax: Int
)

/** Legacy single-pair response (kept for backward compat with GET /pair). */
@Serializable
data class PairResponse(
    val pairId: String = "",
    val person1: RankedPerson,
    val person2: RankedPerson,
    val votesUsed: Int = 0,
    val votesMax: Int = 26
)

@Serializable
data class VoteResponse(
    val winner: RankedPerson,
    val loser: RankedPerson
)

@Serializable
data class VerifyCodeResponse(
    val userId: String,
    val sessionId: String,
    val expiresAt: String
)

@Serializable
data class MeResponse(
    val userId: String,
    val email: String
)

// --- Onboarding ---

@Serializable
data class OnboardingStatusResponse(val completed: Boolean, val rating: Double? = null)

@Serializable
data class OnboardingResponse(val rating: Double)

// --- Admin ---

@Serializable
data class AdminPersonItem(
    val id: String,
    val name: String,
    val elo: Double,
    val sourceType: String,
    val sourceId: String,
    val overrideName: String? = null
)

@Serializable
data class NameOverride(
    val sourceType: String,
    val sourceId: String,
    val overrideName: String
)

@Serializable
data class AdminListsResponse(
    val people: List<AdminPersonItem>,
    val onboarding: List<AdminPersonItem>
)

@Serializable
data class RosterEntry(
    val id: String,
    val name: String,
    val elo: Double
)

@Serializable
data class TournamentStartedResponse(val participantCount: Int)

@Serializable
data class TournamentConfig(
    val numberOfSwitches: Int = 3,
    val numberOfPlayersPerMatch: Int = 4,
    val rounds: Int = 10,
    val podiumWaitTimeInSeconds: Int = 300
)

@Serializable
internal data class AddToRosterBody(
    val name: String,
    val elo: Double,
    val sourceType: String,
    val sourceId: String? = null
)

// --- Request bodies ---

@Serializable
internal data class RequestCodeBody(val email: String)

@Serializable
internal data class OnboardingBody(
    val nickname: String,
    val gamingLevel: Int,
    val ssbExperience: Int,
    val skillLevel: Int
)

@Serializable
internal data class VerifyCodeBody(val email: String, val code: String)

@Serializable
internal data class VoteBody(
    val votePairId: String,
    val winnerPersonId: String,
    val loserPersonId: String
)

@Serializable
internal data class SkipBody(val votePairId: String)

@Serializable
internal data class SetNameOverrideBody(
    val sourceType: String,
    val sourceId: String,
    val overrideName: String
)

// --- Tournament Trust / Results ---

@Serializable
data class TrustStandingEntry(
    val playerName: String,
    val wins: Int,
    val stocks: Int,
    val elo: Double,
)

@Serializable
data class TrustRoundSummary(
    val roundIndex: Int,
    /** Match result strings, e.g. "Alice, Bob vs Charlie, Dave: 3 - 1" */
    val matches: List<String>,
    /** Standings after this round keyed by level: "ADVANCED", "INTERMEDIATE", "BEGINNER" */
    val standingsByLevel: Map<String, List<TrustStandingEntry>>,
)

@Serializable
data class TrustSummaryResponse(
    val rounds: List<TrustRoundSummary>,
)
